import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ShareIcon from '@mui/icons-material/Share';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import Snackbar from '@mui/material/Snackbar';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useCredits } from '../../contexts/CreditContext';
import { reportsApi, fetchAgencyLogoDataUrl } from '../../services/reports';
import { formatDate } from '../../i18n/format';
import LanguageSelect from '../../components/common/LanguageSelect';
import { type UiLanguage } from '../../i18n/languages';
import {
  CREDIT_COST,
  canAccessReports,
  applyAuditTemplate,
  applyStatusTemplate,
  type ReportFormat,
  type ReportKind,
  type ReportTemplate,
  type StoredReport,
} from '@traaker/shared';

const TIMELINE_PRESETS: Record<string, { labelKey: string; days: number }> = {
  '30d': { labelKey: 'reportLibrary.period.30d', days: 30 },
  '90d': { labelKey: 'reportLibrary.period.90d', days: 90 },
  '180d': { labelKey: 'reportLibrary.period.180d', days: 180 },
  '365d': { labelKey: 'reportLibrary.period.365d', days: 365 },
};

/**
 * Report library — the single generation hub (two panes, signup-split style):
 *   left  = generate a report: kind (audit snapshot / status timeline) × period
 *           (status only) × format → renders in the browser, masked to the
 *           tenant template, uploads to S3, bills GENERATION (agency).
 *   right = the tenant's stored reports: download (free), share, rename, delete.
 */
export default function ReportLibrary() {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const { isAgency, refresh: refreshCredits } = useCredits();
  const [reports, setReports] = useState<StoredReport[]>([]);
  const [template, setTemplate] = useState<ReportTemplate | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<ReportKind>('audit');
  const [period, setPeriod] = useState<string>('90d');
  const [format, setFormat] = useState<ReportFormat>('pdf');
  // Report language — defaults to the brand working language, overridable per
  // export. Re-syncs when the active brand changes (unless the user overrode it
  // this session, tracked by langTouched).
  const brandWorkingLang: UiLanguage =
    activeBrand?.strategy?.workingLanguage === 'en' ? 'en' : 'fr';
  const [language, setLanguage] = useState<UiLanguage>(brandWorkingLang);
  const [langTouched, setLangTouched] = useState(false);
  useEffect(() => {
    if (!langTouched) setLanguage(brandWorkingLang);
  }, [brandWorkingLang, langTouched]);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const reportsEnabled = canAccessReports(tenant?.tier, tenant?.settings?.featureFlags?.reportsEnabled);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { reports } = await reportsApi.listLibrary();
      setReports(reports);
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!reportsEnabled) return;
    loadList();
    reportsApi
      .getTemplate()
      .then((r) => setTemplate(r.template))
      .catch(() => setTemplate(undefined)); // no template ⇒ everything renders
  }, [reportsEnabled, loadList]);

  if (!reportsEnabled) {
    return <Alert severity="info">{t('reportLibrary.notEnabled')}</Alert>;
  }

  const generate = async () => {
    if (!activeBrand) {
      setError(t('reportLibrary.selectBrand'));
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const logo = await fetchAgencyLogoDataUrl();
      const mod = await import('../../services/reportExport');
      // 1. Fetch the data payload, 2. mask it to the tenant template (disabled
      //    sections are dropped — renderers skip absent blocks), 3. render to a
      //    blob in-browser.
      let blob: Blob;
      let defaultName: string;
      if (kind === 'audit') {
        const payload = applyAuditTemplate(await reportsApi.audit(activeBrand.brandId, language), template);
        blob =
          format === 'pdf'
            ? await mod.renderAuditReportPdf(payload, logo)
            : await mod.renderAuditReportPptx(payload, logo);
        defaultName = t('reportLibrary.name.audit', { brand: activeBrand.name });
      } else {
        const { windowStart, windowEnd } = computeWindow(period);
        const payload = applyStatusTemplate(
          await reportsApi.status({ brandId: activeBrand.brandId, windowStart, windowEnd, language }),
          template,
        );
        blob =
          format === 'pdf'
            ? await mod.renderStatusReportPdf(payload, logo)
            : await mod.renderStatusReportPptx(payload, logo);
        defaultName = t('reportLibrary.name.status', {
          period: t(TIMELINE_PRESETS[period].labelKey),
          brand: activeBrand.name,
        });
      }
      // 3. Upload → store + bill generation.
      await reportsApi.generate({
        brandId: activeBrand.brandId,
        brandName: activeBrand.name,
        kind,
        format,
        name: defaultName,
        blob,
      });
      await loadList();
      if (isAgency) refreshCredits();
    } catch (e: any) {
      setError(e?.body?.message || e?.message || t('reportLibrary.error.generate'));
    } finally {
      setGenerating(false);
    }
  };

  const download = async (r: StoredReport) => {
    setBusyId(r.reportId);
    try {
      await reportsApi.download(r.reportId, r.name, r.format);
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.download'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: StoredReport) => {
    setBusyId(r.reportId);
    try {
      await reportsApi.remove(r.reportId);
      setReports((prev) => prev.filter((x) => x.reportId !== r.reportId));
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.delete'));
    } finally {
      setBusyId(null);
    }
  };

  const commitRename = async (r: StoredReport) => {
    const name = renameValue.trim();
    setRenameId(null);
    if (!name || name === r.name) return;
    try {
      const { report } = await reportsApi.rename(r.reportId, name);
      setReports((prev) => prev.map((x) => (x.reportId === r.reportId ? report : x)));
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.rename'));
    }
  };

  // Mint (or rotate) a public 30-day link and copy it to the clipboard.
  const share = async (r: StoredReport) => {
    setBusyId(r.reportId);
    try {
      const { url, expiresAt } = await reportsApi.share(r.reportId);
      try {
        await navigator.clipboard.writeText(url);
        setToast(t('reportLibrary.toast.linkCopied'));
      } catch {
        // Clipboard can be blocked (insecure context); show the URL to copy manually.
        setToast(url);
      }
      setReports((prev) => prev.map((x) => (x.reportId === r.reportId ? { ...x, shareExpiresAt: expiresAt } : x)));
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.share'));
    } finally {
      setBusyId(null);
    }
  };

  const revokeShare = async (r: StoredReport) => {
    setBusyId(r.reportId);
    try {
      await reportsApi.revokeShare(r.reportId);
      setReports((prev) => prev.map((x) => (x.reportId === r.reportId ? { ...x, shareExpiresAt: undefined } : x)));
      setToast(t('reportLibrary.toast.linkRevoked'));
    } catch (e: any) {
      setError(e?.message || t('reportLibrary.error.revoke'));
    } finally {
      setBusyId(null);
    }
  };

  const isShared = (r: StoredReport) =>
    !!r.shareExpiresAt && new Date(r.shareExpiresAt).getTime() > Date.now();

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
      {/* Left — generate */}
      <Card sx={{ flex: '0 0 320px', width: '100%' }} data-tour="report-generate">
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{t('reportLibrary.generateTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('reportLibrary.forBrand')} <strong>{activeBrand?.name || '—'}</strong>
          </Typography>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('reportLibrary.typeLabel')}</Typography>
          <Select fullWidth size="small" value={kind} onChange={(e) => setKind(e.target.value as ReportKind)} sx={{ mb: 2 }}>
            <MenuItem value="audit">{t('reportLibrary.type.audit')}</MenuItem>
            <MenuItem value="status">{t('reportLibrary.type.status')}</MenuItem>
          </Select>

          {kind === 'status' && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('reportLibrary.periodLabel')}</Typography>
              <Select fullWidth size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ mb: 2 }}>
                {Object.entries(TIMELINE_PRESETS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{t(v.labelKey)}</MenuItem>
                ))}
              </Select>
            </>
          )}

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('reportLibrary.formatLabel')}</Typography>
          <Select fullWidth size="small" value={format} onChange={(e) => setFormat(e.target.value as ReportFormat)} sx={{ mb: 2 }}>
            <MenuItem value="pdf">{t('reportLibrary.format.pdf')}</MenuItem>
            <MenuItem value="pptx">{t('reportLibrary.format.pptx')}</MenuItem>
          </Select>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('reportLibrary.languageLabel')}</Typography>
          <Box sx={{ mb: 2 }}>
            <LanguageSelect
              value={language}
              onChange={(l) => { setLanguage(l); setLangTouched(true); }}
              fullWidth
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            disabled={generating || !activeBrand}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={generate}
          >
            {generating ? t('reportLibrary.generating') : t('reportLibrary.generateAndSave')}
          </Button>
          {isAgency && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('reportLibrary.creditNote', { credits: CREDIT_COST.report_export })}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Right — library */}
      <Card sx={{ flex: 1, width: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>{t('reportLibrary.myReports')}</Typography>
            {loading && <CircularProgress size={18} />}
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Divider sx={{ mb: 1 }} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('reportLibrary.col.name')}</TableCell>
                <TableCell>{t('reportLibrary.col.type')}</TableCell>
                <TableCell>{t('reportLibrary.col.format')}</TableCell>
                <TableCell>{t('reportLibrary.col.date')}</TableCell>
                <TableCell align="right">{t('reportLibrary.col.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.reportId}>
                  <TableCell>
                    {renameId === r.reportId ? (
                      <TextField
                        size="small"
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(r)}
                        onKeyDown={(e) => e.key === 'Enter' && commitRename(r)}
                      />
                    ) : (
                      <>
                        {r.name}
                        {r.brandName ? <Typography variant="caption" color="text.secondary" display="block">{r.brandName}</Typography> : null}
                        {isShared(r) && (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            icon={<ShareIcon sx={{ fontSize: 14 }} />}
                            label={t('reportLibrary.shareChip', { date: formatDate(r.shareExpiresAt!) })}
                            sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { fontSize: 11, px: 0.75 } }}
                          />
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={r.kind === 'audit' ? t('reportLibrary.kind.audit') : t('reportLibrary.kind.status')} /></TableCell>
                  <TableCell>
                    {r.format === 'pdf' ? <PictureAsPdfIcon fontSize="small" /> : <SlideshowIcon fontSize="small" />}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                    {formatDate(r.createdAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('reportLibrary.action.download')}>
                      <span>
                        <IconButton size="small" disabled={busyId === r.reportId} onClick={() => download(r)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isShared(r) ? t('reportLibrary.action.copyLink') : t('reportLibrary.action.share')}>
                      <span>
                        <IconButton size="small" disabled={busyId === r.reportId} onClick={() => share(r)}>
                          <ShareIcon fontSize="small" color={isShared(r) ? 'success' : 'inherit'} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {isShared(r) && (
                      <Tooltip title={t('reportLibrary.action.revoke')}>
                        <span>
                          <IconButton size="small" disabled={busyId === r.reportId} onClick={() => revokeShare(r)}>
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={t('reportLibrary.action.rename')}>
                      <IconButton size="small" onClick={() => { setRenameId(r.reportId); setRenameValue(r.name); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('reportLibrary.action.delete')}>
                      <span>
                        <IconButton size="small" disabled={busyId === r.reportId} onClick={() => remove(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!reports.length && !loading && (
                <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">{t('reportLibrary.empty')}</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

/** Resolve a preset key to an ISO window ending now. */
function computeWindow(presetKey: string): { windowStart: string; windowEnd: string } {
  const days = TIMELINE_PRESETS[presetKey]?.days ?? 90;
  const end = new Date();
  const start = new Date(end.getTime() - days * 86400_000);
  return { windowStart: start.toISOString(), windowEnd: end.toISOString() };
}
